import { ApolloError } from "@apollo/client";
import { Toast, useReferencedState } from "@aurem/ui";
import { useUserData } from "graphql/user";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useDeleteAllNotifications,
  useFetchNotifications,
  useMarkAllAsRead,
  useMarkAsRead,
  useMarkNotificationsAsSeen,
} from "../hooks";
import type { Notification } from "../types";
import type { NotificationContextType } from "./";

const LIMIT = 20;

export const useViewModel = (): NotificationContextType => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const initialLoading = useReferencedState(true);
  const fetching = useReferencedState(false);
  const isCompleted = useRef(false);

  const pagination = useRef({
    offset: 0,
    limit: LIMIT,
  });

  const { fetchNotifications } = useFetchNotifications();
  const { markAsReadMutation } = useMarkAsRead();
  const { markAllAsReadMutation } = useMarkAllAsRead();
  const { deleteAllMutation } = useDeleteAllNotifications();
  const { markNotificationsAsSeen } = useMarkNotificationsAsSeen();
  const { user } = useUserData();

  const newCount = useMemo(() => {
    const unseenCount = user?.unseenNotificationsCount;
    if (unseenCount != null) {
      return unseenCount;
    }
    return 0;
  }, [user]);

  const fetchPaginatedNotifications = useCallback(async () => {
    if (fetching.current || isCompleted.current) return;

    if (!initialLoading.current) {
      fetching.set(true);
    }

    try {
      const response = await fetchNotifications(pagination.current);
      setNotifications((prev) => [...prev, ...response.list]);
      pagination.current.offset += LIMIT;

      if (response.count <= pagination.current.offset) {
        isCompleted.current = true;
      }
    } catch (error) {
      Toast.error({ title: (error as ApolloError).message });
    } finally {
      initialLoading.set(false);
      fetching.set(false);
    }
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsReadMutation(notificationId);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, readAt: new Date().toISOString() }
              : n,
          ),
        );
      } catch (error) {
        Toast.error({ title: (error as ApolloError).message });
      }
    },
    [markAsReadMutation],
  );

  const markAllAsRead = useCallback(async () => {
    const previousNotifications = notifications;
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        readAt: n.readAt ?? new Date().toISOString(),
      })),
    );

    try {
      await markAllAsReadMutation();
    } catch (error) {
      setNotifications(previousNotifications);
      Toast.error({ title: (error as ApolloError).message });
    }
  }, [notifications, markAllAsReadMutation]);

  const markVisibleNotificationsAsSeen = useCallback(async () => {
    const visibleUnseenIds = notifications
      .filter((n) => !n.readAt)
      .map((n) => n.id);

    if (!visibleUnseenIds.length) return;

    try {
      await markNotificationsAsSeen(visibleUnseenIds);
    } catch (error) {
      Toast.error({ title: (error as ApolloError).message });
    }
  }, [markNotificationsAsSeen, notifications]);

  const deleteAll = useCallback(async () => {
    const previousNotifications = notifications;
    const previousIsCompleted = isCompleted.current;
    setNotifications([]);
    isCompleted.current = true;

    try {
      await deleteAllMutation();
    } catch (error) {
      setNotifications(previousNotifications);
      isCompleted.current = previousIsCompleted;
      Toast.error({ title: (error as ApolloError).message });
    }
  }, [notifications, deleteAllMutation]);

  const refetch = useCallback(() => {
    pagination.current.offset = 0;
    isCompleted.current = false;
    initialLoading.set(true);
    setNotifications([]);
    void fetchPaginatedNotifications();
  }, [fetchPaginatedNotifications]);

  useEffect(() => {
    void fetchPaginatedNotifications();
  }, [fetchPaginatedNotifications]);

  const handleOpenChange = useCallback(
    (newValue: boolean | ((prev: boolean) => boolean)) => {
      setIsOpen(newValue);

      if (newValue && !isOpen) {
        void markVisibleNotificationsAsSeen();
      }
    },
    [isOpen, markVisibleNotificationsAsSeen],
  );

  return useMemo(
    (): NotificationContextType => ({
      notifications,
      initialLoading: initialLoading.value,
      loading: fetching.value,
      unreadCount: newCount,
      hasMore: !isCompleted.current,
      isOpen,
      setIsOpen: handleOpenChange,
      fetchPaginatedNotifications,
      markAsRead,
      markAllAsRead,
      deleteAll,
      refetch,
    }),
    [
      notifications,
      initialLoading.value,
      fetching.value,
      newCount,
      isOpen,
      handleOpenChange,
      fetchPaginatedNotifications,
      markAsRead,
      markAllAsRead,
      deleteAll,
      refetch,
    ],
  );
};
